import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { clarityEvent } from '../utils/clarity';
import './QuoteViewPage.css';

const fmt = (n) =>
    n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : '—';

// ── Quote item card ───────────────────────────────────────────
const QuoteItemCard = ({ item, index }) => {
    const gstRate = item.gst || 0;
    const mrp     = item.mrp || 0;

    // ── PRICING MODEL — read before touching this block ──────────────────────────
    // eYantra operates across two pricing worlds:
    //
    //   BRAND WORLD   — MRP comes directly from brands, always INCLUSIVE of GST.
    //                   Never add GST to mrp. Use it as-is for display.
    //
    //   TRADE WORLD   — marketOperatingPrice and eYantraDiscountedPrice are
    //                   negotiated with vendors, always EXCLUSIVE of GST.
    //                   Always add GST before displaying to the client.
    //
    // What the backend sends on each quote item:
    //   item.price                   → eYantraDiscountedPrice (excl. GST)  if employee set a custom price
    //                                  OR marketOperatingPriceWithGst (incl. GST) as fallback
    //   item.marketOperatingPriceWithGst → standard MOP incl. GST (always incl. GST, used for strikethrough)
    //   item.mrp                     → brand MRP (always incl. GST, ceiling price)
    //
    // Detection: when item.price < item.marketOperatingPriceWithGst, it must be
    // the excl-GST discount price → add GST. Otherwise it's already incl. GST → use as-is.
    // ─────────────────────────────────────────────────────────────────────────────
    const isDiscounted = item.price && item.marketOperatingPriceWithGst
        && item.price < item.marketOperatingPriceWithGst;

    const unitPriceInclGst_raw = isDiscounted
        ? item.price * (1 + (gstRate / 100))                        // excl. GST → add GST
        : (item.price || item.marketOperatingPriceWithGst || 0);    // already incl. GST

    // Round only for the per-unit display label ("@ ₹X") — never use this for total calculations.
    // For discounted items item.price is excl. GST, so compute totals from that directly to
    // avoid the rounding loss that occurs when back-calculating base from a rounded incl. price.
    const unitPriceInclGst = Math.round(unitPriceInclGst_raw);

    let totalBaseAmount, totalGstAmount, totalAmountInclGst;
    if (isDiscounted) {
        totalBaseAmount    = item.price * item.quantity;
        totalGstAmount     = Math.round(totalBaseAmount * gstRate / 100);
        totalAmountInclGst = totalBaseAmount + totalGstAmount;
    } else {
        totalAmountInclGst = unitPriceInclGst * item.quantity;
        totalBaseAmount    = totalAmountInclGst / (1 + (gstRate / 100));
        totalGstAmount     = totalAmountInclGst - totalBaseAmount;
    }

    return (
        <div className="qvp-item" style={{ animationDelay: `${0.05 * index}s` }}>
            <div className="qvp-item__num">
                {String(index + 1).padStart(2, '0')}
            </div>
            <div className="qvp-item__img">
                {item.thumbnailUrl
                    ? <img src={item.thumbnailUrl} alt={item.name} />
                    : <span className="qvp-item__placeholder">📦</span>}
            </div>
            <div className="qvp-item__info">
                <div className="qvp-item__name">{item.name}</div>
                <div className="qvp-item__meta">
                    <span className="qvp-item__qty">Qty: {item.quantity}</span>
                    {isDiscounted ? (
                        <>
                            <span className="qvp-item__unit" style={{ textDecoration: 'line-through', opacity: 0.5 }}>@ {fmt(item.marketOperatingPriceWithGst)}</span>
                            <span className="qvp-item__unit" style={{ color: '#ff6b35', fontWeight: 700 }}>@ {fmt(unitPriceInclGst)}</span>
                        </>
                    ) : (
                        <span className="qvp-item__unit">@ {fmt(unitPriceInclGst)}</span>
                    )}
                    {mrp > 0 && unitPriceInclGst < mrp && !isDiscounted && (
                        <span className="qvp-item__mrp">MRP {fmt(mrp)}</span>
                    )}
                </div>
            </div>
            <div className="qvp-item__breakdown">
                <div className="qvp-item__breakdown-row qvp-item__breakdown-row--gst">
                    <span className="qvp-item__breakdown-label">GST {gstRate}%</span>
                    <span className="qvp-item__breakdown-val">{fmt(totalGstAmount)}</span>
                </div>
                <div className="qvp-item__breakdown-row">
                    <span className="qvp-item__breakdown-label">Total (incl. GST)</span>
                    <span className="qvp-item__breakdown-val">{fmt(totalAmountInclGst)}</span>
                </div>
            </div>
            <div className="qvp-item__subtotal">
                {fmt(totalBaseAmount)}
            </div>
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────
const QuoteViewPage = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const prefillName = searchParams.get('name') || '';

    // Gate form state
    const [cartName,     setCartName]     = useState(prefillName);
    const [password,     setPassword]     = useState('');
    const [contactInfo,  setContactInfo]  = useState('');
    const [showPass,     setShowPass]     = useState(false);
    const [isLoading,    setIsLoading]    = useState(false);
    const [error,        setError]        = useState('');

    // Quote data state
    const [quote,        setQuote]        = useState(null);
    const printRef = useRef(null);

    useEffect(() => {
        if (user && prefillName && !quote) {
            setIsLoading(true);
            api.post('/quotes/access', {
                cartName: prefillName,
                password: '',
                contactInfo: user.email
            })
            .then(res => {
                setQuote(res.data);
                clarityEvent('quote_viewed');
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
        }
    }, [user, prefillName, quote]);

    const handleAccess = async (e) => {
        e.preventDefault();
        if (!cartName.trim())    { setError('Please enter the cart name.'); return; }
        if (!password.trim())    { setError('Please enter the password.'); return; }
        if (!contactInfo.trim()) { setError('Please enter your contact info (email or phone).'); return; }
        setError('');
        setIsLoading(true);
        try {
            const res = await api.post('/quotes/access', {
                cartName: cartName.trim(),
                password,
                contactInfo: contactInfo.trim(),
            });
            setQuote(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                setError('Invalid cart name or password. Please check and try again.');
            } else {
                setError('Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrint = () => {
        const originalTitle = document.title;
        document.title = `eYantra_Quote_${quote.cartName}`;
        window.print();
        setTimeout(() => {
            document.title = originalTitle;
        }, 500);
    };

    // ── Totals Calculation ──
    const { totalBase, totalGst, grandTotal } = useMemo(() => {
        if (!quote || !quote.items) return { totalBase: 0, totalGst: 0, grandTotal: 0 };
        const itemTotals = quote.items.reduce((acc, item) => {
            const gstRate      = item.gst || 0;
            const isDiscounted = item.price && item.marketOperatingPriceWithGst
                && item.price < item.marketOperatingPriceWithGst;

            const unitPriceInclGst_raw = isDiscounted
                ? item.price * (1 + (gstRate / 100))
                : (item.price || item.marketOperatingPriceWithGst || 0);
            const unitPriceInclGst = Math.round(unitPriceInclGst_raw);

            let itemBaseAmount, itemGstAmount, itemTotalAmount;
            if (isDiscounted) {
                itemBaseAmount  = item.price * item.quantity;
                itemGstAmount   = Math.round(itemBaseAmount * gstRate / 100);
                itemTotalAmount = itemBaseAmount + itemGstAmount;
            } else {
                itemTotalAmount = unitPriceInclGst * item.quantity;
                itemBaseAmount  = itemTotalAmount / (1 + (gstRate / 100));
                itemGstAmount   = itemTotalAmount - itemBaseAmount;
            }

            return {
                totalBase:  acc.totalBase  + itemBaseAmount,
                totalGst:   acc.totalGst   + itemGstAmount,
                grandTotal: acc.grandTotal + itemTotalAmount,
            };
        }, { totalBase: 0, totalGst: 0, grandTotal: 0 });

        const deliveryCharge = quote.deliveryCharge || 0;
        const customizationCharge = quote.customizationCharge || 0;
        return {
            ...itemTotals,
            grandTotal: itemTotals.grandTotal + deliveryCharge + customizationCharge,
        };
    }, [quote]);

    // ── Gate screen ───────────────────────────────────────────
    if (!quote) {
        // [KEEP YOUR EXISTING GATE RENDER LOGIC HERE]
        return (
            <div className="qvp-gate-wrapper">
                <div className="qvp-gate-bg" />
                <div className="qvp-gate-card">
                    <div className="qvp-gate-logo"><div className="qvp-gate-logo__icon">📋</div></div>
                    <h1 className="qvp-gate-title">View Your Quote</h1>
                    <p className="qcp-gate-sub">Enter the cart name and password shared by your representative, along with your contact info.</p>
                    <form className="qvp-gate-form" onSubmit={handleAccess}>
                        <div className="qvp-gate-field">
                            <label>Cart Name</label>
                            <input type="text" placeholder="e.g. Acme-Corp-Q1-2025" value={cartName} onChange={e => setCartName(e.target.value)} autoFocus={!prefillName} />
                        </div>
                        <div className="qvp-gate-field">
                            <label>Password</label>
                            <div className="qvp-gate-pass-wrap">
                                <input type={showPass ? 'text' : 'password'} placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} />
                                <button type="button" onClick={() => setShowPass(s => !s)}>{showPass ? '🙈' : '👁'}</button>
                            </div>
                        </div>
                        <div className="qvp-gate-field">
                            <label>Your Contact Info <span>(email or phone)</span></label>
                            <input type="text" placeholder="your@email.com or +91 XXXXX XXXXX" value={contactInfo} onChange={e => setContactInfo(e.target.value)} />
                            <div className="qvp-gate-field__hint">This is recorded so we know who viewed the quote.</div>
                        </div>
                        {error && <div className="qvp-gate-error">{error}</div>}
                        <button type="submit" className="qvp-gate-submit" disabled={isLoading}>
                            {isLoading ? <span className="qvp-gate-spinner" /> : <>🔓 View My Quote</>}
                        </button>
                    </form>
                    <div className="qvp-gate-footer"><span>🔒 Secure · Private · View-only</span></div>
                </div>
            </div>
        );
    }

    // ── Quote display ─────────────────────────────────────────
    const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    // Helper: only render a meta-row if the value is non-empty
    const hasRef = quote.modeTermsOfPayment || quote.suppliersRef || quote.otherReferences ||
        quote.buyerOrderNo || quote.buyerOrderDated || quote.despatchDocumentNo ||
        quote.destination || quote.despatchedType || quote.paymentTerms || quote.termsOfDelivery;

    return (
        <div className="qvp-quote-wrapper" ref={printRef}>
            <div className="qvp-quote-bg" />
            <div className="qvp-quote-doc">
                <div className="qvp-doc-header">
                    <div className="qvp-doc-header__left">
                        <div className="qvp-doc-brand">eYantra</div>
                        <div className="qvp-doc-tagline">Corporate Swag & Premium Gifting</div>
                    </div>
                    <div className="qvp-doc-header__right">
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">QUOTE REFERENCE</span>
                            <span className="qvp-doc-meta-val qvp-doc-meta-val--highlight">{quote.cartName}</span>
                        </div>
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">DATE</span>
                            <span className="qvp-doc-meta-val">{today}</span>
                        </div>
                        <div className="qvp-doc-meta-row">
                            <span className="qvp-doc-meta-label">PREPARED FOR</span>
                            <span className="qvp-doc-meta-val">{quote.quoteCreatedFor || 'Client'}</span>
                        </div>
                    </div>
                </div>
                <div className="qvp-doc-divider" />

                {/* ── Buyer details (shown when filled) ─────────── */}
                {(quote.billingAddress || quote.clientGstin || quote.clientPhone) && (
                    <div className="qvp-buyer-block">
                        <div className="qvp-buyer-block__label">BILL TO</div>
                        <div className="qvp-buyer-block__name">{quote.quoteCreatedFor || '—'}</div>
                        {quote.billingAddress && <div className="qvp-buyer-block__addr">{quote.billingAddress}</div>}
                        <div className="qvp-buyer-block__meta-row">
                            {quote.clientPhone && <span>Ph: {quote.clientPhone}</span>}
                            {quote.clientGstin && <span>GSTIN: {quote.clientGstin}</span>}
                        </div>
                    </div>
                )}

                {/* ── Reference fields (shown when at least one is filled) ── */}
                {hasRef && (
                    <div className="qvp-ref-grid">
                        {quote.modeTermsOfPayment  && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Mode / Terms of Payment</span><span className="qvp-ref-item__val">{quote.modeTermsOfPayment}</span></div>}
                        {quote.paymentTerms        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Payment Terms</span><span className="qvp-ref-item__val">{quote.paymentTerms}</span></div>}
                        {quote.suppliersRef        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Supplier's Ref.</span><span className="qvp-ref-item__val">{quote.suppliersRef}</span></div>}
                        {quote.otherReferences     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Other Reference(s)</span><span className="qvp-ref-item__val">{quote.otherReferences}</span></div>}
                        {quote.buyerOrderNo        && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Buyer's Order No.</span><span className="qvp-ref-item__val">{quote.buyerOrderNo}</span></div>}
                        {quote.buyerOrderDated     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Buyer Order Dated</span><span className="qvp-ref-item__val">{quote.buyerOrderDated}</span></div>}
                        {quote.despatchDocumentNo  && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Despatch Document No.</span><span className="qvp-ref-item__val">{quote.despatchDocumentNo}</span></div>}
                        {quote.destination         && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Destination</span><span className="qvp-ref-item__val">{quote.destination}</span></div>}
                        {quote.despatchedType      && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Despatched through</span><span className="qvp-ref-item__val">{quote.despatchedType}</span></div>}
                        {quote.termsOfDelivery     && <div className="qvp-ref-item"><span className="qvp-ref-item__label">Terms of Delivery</span><span className="qvp-ref-item__val">{quote.termsOfDelivery}</span></div>}
                    </div>
                )}

                <div className="qvp-section-head">
                    <h2 className="qvp-section-head__title">Quoted Items</h2>
                    <span className="qvp-section-head__count">{quote.items.length} products</span>
                </div>
                <div className="qvp-items-header">
                    <span className="qvp-items-header__num">#</span>
                    <span className="qvp-items-header__img" />
                    <span className="qvp-items-header__name">Product</span>
                    <span className="qvp-items-header__breakdown">GST / Total (incl. GST)</span>
                    <span className="qvp-items-header__total">Base (excl. GST)</span>
                </div>
                <div className="qvp-items-list">
                    {quote.items.map((item, i) => (
                        <QuoteItemCard key={item.productId || i} item={item} index={i} />
                    ))}
                </div>
                <div className="qvp-totals">
                    <div className="qvp-totals__row">
                        <span>Subtotal (excl. GST) — {quote.items.reduce((s, i) => s + i.quantity, 0)} items</span>
                        <span>{fmt(totalBase)}</span>
                    </div>
                    <div className="qvp-totals__row qvp-totals__row--note">
                        <span>Total GST</span>
                        <span>{fmt(totalGst)}</span>
                    </div>
                    {quote.deliveryCharge > 0 && (
                        <div className="qvp-totals__row qvp-totals__row--note">
                            <span>Delivery Charges</span>
                            <span>{fmt(quote.deliveryCharge)}</span>
                        </div>
                    )}
                    {quote.customizationCharge > 0 && (
                        <div className="qvp-totals__row qvp-totals__row--note">
                            <span>Customization Charges</span>
                            <span>{fmt(quote.customizationCharge)}</span>
                        </div>
                    )}
                    <div className="qvp-totals__grand">
                        <span>Grand Total (incl. GST)</span>
                        <span className="qvp-totals__grand-val">{fmt(grandTotal)}</span>
                    </div>
                    <div className="qvp-totals__eoe">E &amp; O.E</div>
                </div>

                {/* ── Bank details + signature ───────────────────── */}
                <div className="qvp-bank-sign-row">
                    <div className="qvp-bank">
                        <div className="qvp-bank__title">Company's Bank Details</div>
                        <div className="qvp-bank__row"><span>Bank Name</span><span>AU Small Finance Bank</span></div>
                        <div className="qvp-bank__row"><span>A/c No.</span><span>2606266799636981</span></div>
                        <div className="qvp-bank__row"><span>Branch</span><span>Madhapur, Hyderabad</span></div>
                        <div className="qvp-bank__row"><span>IFSC Code</span><span>AUBL0002667</span></div>
                        <div className="qvp-bank__pan">Company's PAN: <strong>AAACP2025M</strong></div>
                    </div>
                    <div className="qvp-sign">
                        <div className="qvp-sign__for">For eYantra Ventures Limited</div>
                        <div className="qvp-sign__spacer" />
                        <div className="qvp-sign__line" />
                        <div className="qvp-sign__label">Authorised Signatory</div>
                        {quote.preparedBy && <div className="qvp-sign__prepared">Prepared by: {quote.preparedBy}</div>}
                    </div>
                </div>

                <div className="qvp-doc-footer">
                    <div className="qvp-doc-footer__note">
                        <strong>Note:</strong> Final pricing may vary based on order quantity, customisation and delivery terms. This quote is valid for 30 days.
                    </div>
                    <div className="qvp-doc-footer__actions no-print">
                        <button className="qvp-action-btn qvp-action-btn--print" onClick={handlePrint}>🖨 Print / Save PDF</button>
                        <button className="qvp-action-btn qvp-action-btn--share" onClick={() => navigator.clipboard.writeText(window.location.href)}>🔗 Copy Link</button>
                    </div>
                    <div className="qvp-doc-footer__brand">eYantra · Corporate Gifting & Swag · Pan-India Delivery</div>
                </div>
            </div>
        </div>
    );
};

export default QuoteViewPage;